import { useEffect } from "react";
import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from "web-vitals";

interface WebVitalsConfig {
  enabled?: boolean;
  debug?: boolean;
  reportToAnalytics?: boolean;
}

const defaultConfig: WebVitalsConfig = {
  enabled: true,
  debug: false,
  reportToAnalytics: true,
};

export const useWebVitals = (config: WebVitalsConfig = defaultConfig) => {
  useEffect(() => {
    if (!config.enabled) return;

    const handleMetric = (metric: Metric) => {
      try {
        if (config.debug) {
          console.log(`[Web Vitals] ${metric.name}:`, metric.value, metric);
        }

        // Report to analytics if enabled
        if (config.reportToAnalytics && typeof window !== "undefined") {
          // Send to Vercel Analytics
          if (window.va && typeof window.va === "function") {
            try {
              window.va("event", "Web Vitals", {
                metric_name: metric.name,
                metric_value: metric.value,
                metric_rating: metric.rating || "unknown",
              });
            } catch (error) {
              console.warn("Failed to send metric to Vercel Analytics:", error);
            }
          }

          // Send to Google Analytics if available
          if (window.gtag && typeof window.gtag === "function") {
            try {
              window.gtag("event", metric.name, {
                event_category: "Web Vitals",
                value: Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value),
                event_label: metric.id,
                non_interaction: true,
              });
            } catch (error) {
              console.warn("Failed to send metric to Google Analytics:", error);
            }
          }
        }
      } catch (error) {
        console.warn("Error handling Web Vitals metric:", error);
      }
    };

    try {
      // Measure all Web Vitals with error handling
      onCLS(handleMetric);
      onINP(handleMetric); // Interaction to Next Paint (replaces FID in v5)
      onFCP(handleMetric);
      onLCP(handleMetric);
      onTTFB(handleMetric);
    } catch (error) {
      console.warn("Error initializing Web Vitals:", error);
    }
  }, [config.enabled, config.debug, config.reportToAnalytics]);
};

// Type declarations for global analytics
declare global {
  interface Window {
    va?: (event: string, name: string, data?: Record<string, unknown>) => void;
    gtag?: (command: string, action: string, parameters?: Record<string, unknown>) => void;
  }
}
